import { cn } from 'cn';
import { useTranslation } from 'react-i18next';

import { useQueryAnalyticsChart } from '~/data/analytics';

import { getFormattedNumber } from '@vestigas/utils';
import { i18n } from '~/utils';

import { HeadingRow, Icon, Tooltip, withErrorBoundary } from '@vestigas/ui';

import { formatDateForChartTooltip, useDateRange } from '../utils';

import { ChartLegend, getLegendData } from './ChartLegend';
import { DataCard } from './DataCard';
import { TimeSeriesChart } from './TimeSeriesChart';

const CardContent = withErrorBoundary(
  () => {
    const { t } = useTranslation('analytics');

    const dateRange = useDateRange();
    const weeklyUsageChart = useQueryAnalyticsChart({
      dateRange,
      name: 'weekly_active_users',
    });

    const labelTranslationsMap = {
      activeUsers: t(($) => $.usage.datasetLabel.activeUsers),
    };

    return (
      <>
        <DataCard.Header>
          <HeadingRow
            heading={
              <div className="flex items-center">
                {t(($) => $.usage.weeklyUsageChart.heading)}
                <Tooltip
                  content={t(($) => $.usage.weeklyUsageChart.subHeading)}
                >
                  <span className="inline-block -translate-y-1 pl-1">
                    <Icon
                      className="cursor-help text-tiga-action-default transition-colors duration-300 hover:text-tiga-action-lighter"
                      metaphor="infoOutlined"
                      size="sm"
                    />
                  </span>
                </Tooltip>
              </div>
            }
            variant="h3"
          />
        </DataCard.Header>
        <DataCard.Body className="py-0" isLoading={weeklyUsageChart.isFetching}>
          <TimeSeriesChart
            aggregationInterval="month"
            data={weeklyUsageChart.data}
            formatTooltipLabel={(context) => {
              return t(($) => $.usage.weeklyUsageChart.tooltipLabel, {
                value: getFormattedNumber({
                  value: context.parsed.y,
                  digits: 0,
                }),
                date: formatDateForChartTooltip(context.raw.x),
              });
            }}
            formatYAxisLabel={(value: number) =>
              getFormattedNumber({
                value,
                digits: 0,
              })
            }
            formatTooltipTitle={() => ''}
            suggestedMax={20} // Set a y-axis scale max value of at least 20 to avoid ridiculous scaling.
            showDataLabels
          />
        </DataCard.Body>
        <DataCard.Footer className="pt-2">
          <ChartLegend
            data={getLegendData(weeklyUsageChart.data, labelTranslationsMap)}
          />
        </DataCard.Footer>
      </>
    );
  },
  i18n.t(($) => $.errorBoundary.fallbackMessage, {
    ns: 'analytics',
    section: i18n.t(($) => $.usage.weeklyUsageChart.heading, {
      ns: 'analytics',
    }),
  }),
  false,
  'row-span-3',
);

CardContent.displayName = 'UserActivityChartContent';

/**
 * UserActivityChart
 *
 * Displays a time series chart visualizing user activity (weekly active users)
 * for the selected date range.
 */
export const UserActivityChart = ({ className, style }: ComponentStyling) => {
  return (
    <DataCard
      className={cn('bg-tiga-surface-off-white', className)}
      style={style}
    >
      <CardContent />
    </DataCard>
  );
};
